import React, { useState } from 'react';
import { useLandingPage } from '../../contexts/LandingPageContext';
import { getLocalizedText, setLocalizedText, isLocalized } from '../../utils/localized';
import { useImageList } from '../../hooks/useImageList';
import { uploadImageFile } from '../../utils/image';
import { notifyPreviewRefresh } from '../../lib/previewBus';
import LangTag from '../LangTag';

/* ------------------------------------------------------------------ */
/* Shared primitives                                                   */
/* ------------------------------------------------------------------ */

/**
 * One hook per editor: section state + localized read/write helpers.
 * `l(de, en)` picks the label for the language being edited.
 */
const useSectionEditor = (stateKey) => {
  const ctx = useLandingPage();
  const section = ctx[stateKey] || {};
  const { editLanguage, updateSection } = ctx;

  const set = (key, value) => updateSection(stateKey, { ...section, [key]: value });
  const loc = (key) => getLocalizedText(section[key], editLanguage);
  const setLoc = (key, value) => set(key, setLocalizedText(section[key], editLanguage, value));
  const l = (de, en) => (editLanguage === 'en' ? en : de);

  return { section, editLanguage, set, loc, setLoc, l };
};

/** Card wrapper owning the save flow so editors contain zero submit boilerplate. */
const EditorCard = ({ title, stateKey, children }) => {
  const { saveSection } = useLandingPage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ok = await saveSection(stateKey);
    notifyPreviewRefresh();
    alert(ok ? `${title} saved` : 'Save failed');
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="section">
        <h3>{title}</h3>
        <div className="controls">{children}</div>
        <button type="submit" className="submit-btn">Save</button>
      </div>
    </form>
  );
};

const Field = ({ label, tag, children }) => (
  <label className="field">
    <span className="label-title">{label}{tag || null}</span>
    {children}
  </label>
);

const TextField = ({ label, tag, value, onChange, type = 'text', placeholder, rows }) => (
  <Field label={label} tag={tag}>
    {rows ? (
      <textarea rows={rows} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    ) : (
      <input type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    )}
  </Field>
);

/** Text field bound to one language of a localized value; shows a DE/EN badge. */
const LocalizedTextField = ({ label, lang, value, onChange, rows }) => (
  <TextField label={label} tag={<LangTag lang={lang} />} value={value} onChange={onChange} rows={rows} />
);

const ColorField = ({ label, value, onChange }) => (
  <Field label={label}>
    <input type="color" className="color-input" value={value} onChange={(e) => onChange(e.target.value)} />
  </Field>
);

/** Localized value whose payload is an array of lines (stacked text, modal lines). */
const useLocalizedLines = (section, editLanguage, set) => {
  const read = (value) => {
    if (isLocalized(value)) {
      const v = value[editLanguage];
      return Array.isArray(v) ? v.join('\n') : v || '';
    }
    return Array.isArray(value) ? value.join('\n') : value || '';
  };
  const write = (key, text) => {
    const current = section[key];
    const lines = text.split('\n');
    const base = isLocalized(current)
      ? current
      : { de: Array.isArray(current) ? current : [], en: Array.isArray(current) ? current : [] };
    set(key, { ...base, [editLanguage]: lines });
  };
  return { read, write };
};

/** Drag-reorderable image grid with add/remove/upload. */
const ImageListEditor = ({ images, onChange, prefix }) => {
  const list = useImageList(images, onChange);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      list.appendImage(await uploadImageFile(file, `${prefix}_${Date.now()}`));
    } catch {
      alert('Upload failed');
    }
    e.target.value = '';
  };

  return (
    <div className="image-list-editor">
      <div className="image-grid-preview">
        {images.map((image, index) => (
          <div
            key={index}
            className={`image-item-preview${list.draggedIndex === index ? ' dragging' : ''}`}
            draggable
            onDragStart={(e) => list.dragHandlers.onDragStart(e, index)}
            onDragEnd={list.dragHandlers.onDragEnd}
            onDragOver={list.dragHandlers.onDragOver}
            onDrop={(e) => list.dragHandlers.onDrop(e, index)}
          >
            <div className="drag-handle"><span>&#8942;&#8942;</span><span className="image-rank">#{index + 1}</span></div>
            <div className="image-preview">
              {image && <img src={image} alt={`#${index + 1}`} onError={(e) => { e.target.style.display = 'none'; }} />}
            </div>
            <div className="image-controls">
              <input type="text" value={image} placeholder="/images/… or URL" onChange={(e) => list.updateImage(index, e.target.value)} />
              <button type="button" className="btn-remove" onClick={() => list.removeImage(index)}>&times;</button>
            </div>
          </div>
        ))}
      </div>
      <div className="image-list-actions">
        <button type="button" className="btn-add" onClick={list.addImage}>Add image</button>
        <span className="btn-upload">
          Upload image
          <input type="file" accept="image/*" onChange={handleUpload} />
        </span>
      </div>
    </div>
  );
};

const imagesOf = (section) => (Array.isArray(section.images) ? section.images : []);

/* ------------------------------------------------------------------ */
/* Section editors                                                     */
/* ------------------------------------------------------------------ */

const HeroEditor = () => {
  const { section, editLanguage, set, loc, setLoc, l } = useSectionEditor('hero');

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      set('image', await uploadImageFile(file, `hero_${Date.now()}`, { compress: false }));
    } catch {
      alert('Upload failed');
    }
  };

  return (
    <EditorCard title="Hero" stateKey="hero">
      <LocalizedTextField label={l('Titel', 'Title')} lang={editLanguage} value={loc('title')} onChange={(v) => setLoc('title', v)} />
      <LocalizedTextField label={l('Untertitel', 'Subtitle')} lang={editLanguage} value={loc('subtitle')} onChange={(v) => setLoc('subtitle', v)} />
      <TextField label="Image URL" value={section.image || ''} onChange={(v) => set('image', v)} />
      <Field label={l('Bild hochladen', 'Upload image')}>
        <input type="file" accept="image/*" onChange={handleUpload} />
      </Field>
      <ColorField label={l('Hintergrundfarbe', 'Background color')} value={section.backgroundColor || '#ffffff'} onChange={(v) => set('backgroundColor', v)} />
      <ColorField label={l('Textfarbe', 'Text color')} value={section.textColor || '#000000'} onChange={(v) => set('textColor', v)} />
    </EditorCard>
  );
};

const FirstSectionEditor = () => {
  const { section, editLanguage, set, loc, setLoc, l } = useSectionEditor('firstSection');
  const lines = useLocalizedLines(section, editLanguage, set);
  const cards = Array.isArray(section.cards) ? section.cards : [];

  const setCard = (index, key, value) => {
    const next = [...cards];
    next[index] = { ...next[index], [key]: value };
    set('cards', next);
  };

  return (
    <EditorCard title="Idea" stateKey="firstSection">
      <LocalizedTextField label={l('Gestapelter Text', 'Stacked text')} lang={editLanguage} value={lines.read(section.stackedText)} onChange={(v) => lines.write('stackedText', v)} rows={4} />
      <LocalizedTextField label={l('Navigationstext', 'Nav text')} lang={editLanguage} value={loc('navText')} onChange={(v) => setLoc('navText', v)} />
      <LocalizedTextField label={l('Modal-Kopfzeile', 'Modal header')} lang={editLanguage} value={loc('modalHeader')} onChange={(v) => setLoc('modalHeader', v)} rows={3} />
      <div className="color-grid">
        <ColorField label={l('Hintergrund', 'Background')} value={section.backgroundColor || '#ffffff'} onChange={(v) => set('backgroundColor', v)} />
        <ColorField label={l('Text', 'Text')} value={section.textColor || '#000000'} onChange={(v) => set('textColor', v)} />
        <ColorField label={l('Kartentitel', 'Card title')} value={section.cardTitleColor || '#000000'} onChange={(v) => set('cardTitleColor', v)} />
        <ColorField label={l('Kartenbeschreibung', 'Card description')} value={section.cardDescriptionColor || '#000000'} onChange={(v) => set('cardDescriptionColor', v)} />
      </div>

      <h4 className="list-heading">{l('Karten', 'Cards')}</h4>
      {cards.map((card, index) => (
        <div key={card.id || index} className="card-controls">
          <LocalizedTextField label={l('Titel', 'Title')} lang={editLanguage} value={getLocalizedText(card.title, editLanguage)} onChange={(v) => setCard(index, 'title', setLocalizedText(card.title, editLanguage, v))} />
          <LocalizedTextField label={l('Beschreibung', 'Description')} lang={editLanguage} value={getLocalizedText(card.description, editLanguage)} onChange={(v) => setCard(index, 'description', setLocalizedText(card.description, editLanguage, v))} rows={2} />
        </div>
      ))}
    </EditorCard>
  );
};

const ArtSectionEditor = () => {
  const { section, editLanguage, set, loc, setLoc, l } = useSectionEditor('artSection');

  return (
    <EditorCard title="Art" stateKey="artSection">
      <Field label={l('Bilder', 'Images')}>
        <ImageListEditor images={imagesOf(section)} onChange={(next) => set('images', next)} prefix="art" />
      </Field>
      <LocalizedTextField label={l('Vollständiger Text', 'Full text')} lang={editLanguage} value={loc('fullText')} onChange={(v) => setLoc('fullText', v)} rows={4} />
      <LocalizedTextField label={l('Overlay-Text', 'Overlay text')} lang={editLanguage} value={loc('overlayText')} onChange={(v) => setLoc('overlayText', v)} rows={2} />
      <LocalizedTextField label={l('Modaltitel', 'Modal title')} lang={editLanguage} value={loc('modalTitle')} onChange={(v) => setLoc('modalTitle', v)} />
      <LocalizedTextField label={l('Modalinhalt', 'Modal content')} lang={editLanguage} value={loc('modalContent')} onChange={(v) => setLoc('modalContent', v)} rows={3} />
      <div className="color-grid">
        <ColorField label={l('Textfarbe', 'Text color')} value={section.textColor || '#222222'} onChange={(v) => set('textColor', v)} />
        <ColorField label={l('Übriger Text', 'Remaining text')} value={section.remainingTextColor || '#777777'} onChange={(v) => set('remainingTextColor', v)} />
        <ColorField label={l('Overlay-Textfarbe', 'Overlay text color')} value={section.overlayTextColor || '#ffffff'} onChange={(v) => set('overlayTextColor', v)} />
        <ColorField label={l('Modaltitelfarbe', 'Modal title color')} value={section.modalTitleColor || '#ffffff'} onChange={(v) => set('modalTitleColor', v)} />
        <ColorField label={l('Modalinhaltsfarbe', 'Modal content color')} value={section.modalContentColor || '#ffffff'} onChange={(v) => set('modalContentColor', v)} />
      </div>
    </EditorCard>
  );
};

const NowSectionEditor = () => {
  const { section, editLanguage, set, loc, setLoc, l } = useSectionEditor('nowSection');
  const lines = useLocalizedLines(section, editLanguage, set);

  return (
    <EditorCard title="Now" stateKey="nowSection">
      <Field label={l('Bilder', 'Images')}>
        <ImageListEditor images={imagesOf(section)} onChange={(next) => set('images', next)} prefix="now" />
      </Field>
      <LocalizedTextField label={l('Button-Text', 'Button text')} lang={editLanguage} value={loc('buttonText')} onChange={(v) => setLoc('buttonText', v)} />
      <LocalizedTextField label={l('Modaltitel', 'Modal title')} lang={editLanguage} value={loc('modalTitle')} onChange={(v) => setLoc('modalTitle', v)} />
      <LocalizedTextField label={l('Modalinhalt', 'Modal content')} lang={editLanguage} value={lines.read(section.modalContent)} onChange={(v) => lines.write('modalContent', v)} rows={6} />
      <div className="color-grid">
        <ColorField label={l('Button-Textfarbe', 'Button text color')} value={section.buttonTextColor || '#222222'} onChange={(v) => set('buttonTextColor', v)} />
        <ColorField label={l('Modaltitelfarbe', 'Modal title color')} value={section.modalTitleColor || '#ffffff'} onChange={(v) => set('modalTitleColor', v)} />
        <ColorField label={l('Modalinhaltsfarbe', 'Modal content color')} value={section.modalContentColor || '#ffffff'} onChange={(v) => set('modalContentColor', v)} />
      </div>
    </EditorCard>
  );
};

const GallerySectionEditor = () => {
  const { section, set } = useSectionEditor('gallerySection');

  return (
    <EditorCard title="Gallery" stateKey="gallerySection">
      <TextField label="Header title" value={section.headerTitle || ''} onChange={(v) => set('headerTitle', v)} />
      <TextField label="Header subtitle" value={section.headerSubtitle || ''} onChange={(v) => set('headerSubtitle', v)} />
      <ImageListEditor images={imagesOf(section)} onChange={(next) => set('images', next)} prefix="gallery" />
    </EditorCard>
  );
};

const StarRating = ({ rating, onRatingChange, disabled = false }) => (
  <div className="star-rating">
    {[1, 2, 3, 4, 5].map((star) => (
      <span
        key={star}
        className={`star${star <= rating ? ' filled' : ''}${disabled ? ' disabled' : ''}`}
        onClick={() => !disabled && onRatingChange(star)}
      >
        &#9733;
      </span>
    ))}
  </div>
);

const VideosSectionEditor = () => {
  const { section, set } = useSectionEditor('videosSection');
  const [expandedVideo, setExpandedVideo] = useState(null);
  const videos = Array.isArray(section.videos) ? section.videos : [];

  const setVideos = (next) => set('videos', next);

  const setVideo = (index, key, value) => {
    const next = [...videos];
    next[index] = { ...next[index], [key]: value };
    setVideos(next);
  };

  const addVideo = () => {
    setVideos([...videos, { id: Date.now(), customer: '', title: '', thumbnail: '', videoUrl: '', comment: '', rating: 5 }]);
    setExpandedVideo(videos.length);
  };

  const removeVideo = (index) => {
    if (!window.confirm('Remove this video?')) return;
    setVideos(videos.filter((_, i) => i !== index));
    if (expandedVideo === index) setExpandedVideo(null);
    else if (expandedVideo > index) setExpandedVideo(expandedVideo - 1);
  };

  const moveVideo = (from, to) => {
    const next = [...videos];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setVideos(next);
    if (expandedVideo === from) setExpandedVideo(to);
    else if (expandedVideo === to) setExpandedVideo(from);
  };

  return (
    <EditorCard title="Videos" stateKey="videosSection">
      <TextField label="Header title" value={section.headerTitle || ''} onChange={(v) => set('headerTitle', v)} />
      <TextField label="Header subtitle" value={section.headerSubtitle || ''} onChange={(v) => set('headerSubtitle', v)} />

      <div className="videos-section">
        <div className="videos-header">
          <h4>Videos ({videos.length})</h4>
          <button type="button" className="btn-add" onClick={addVideo}>Add video</button>
        </div>
        <div className="videos-list">
          {videos.map((video, index) => (
            <div key={video.id} className="video-item">
              <div className="video-header" onClick={() => setExpandedVideo(expandedVideo === index ? null : index)}>
                <div className="video-summary">
                  <div className="video-info">
                    <h5>{video.customer || 'Unnamed'}</h5>
                    <p>{video.title || 'No title'}</p>
                    <div className="rating-display"><StarRating rating={video.rating} onRatingChange={() => {}} disabled /></div>
                  </div>
                </div>
                <div className="video-actions">
                  <button type="button" className="btn-icon" disabled={index === 0} onClick={(e) => { e.stopPropagation(); moveVideo(index, Math.max(0, index - 1)); }}>&#11014;</button>
                  <button type="button" className="btn-icon" disabled={index === videos.length - 1} onClick={(e) => { e.stopPropagation(); moveVideo(index, Math.min(videos.length - 1, index + 1)); }}>&#11015;</button>
                  <button type="button" className="btn-icon danger" onClick={(e) => { e.stopPropagation(); removeVideo(index); }}>&#128465;</button>
                </div>
              </div>
              {expandedVideo === index && (
                <div className="video-details">
                  <div className="form-grid">
                    <TextField label="Customer" value={video.customer} onChange={(v) => setVideo(index, 'customer', v)} />
                    <TextField label="Title" value={video.title} onChange={(v) => setVideo(index, 'title', v)} />
                    <TextField label="Thumbnail URL" type="url" value={video.thumbnail} onChange={(v) => setVideo(index, 'thumbnail', v)} />
                    <TextField label="Video URL" type="url" value={video.videoUrl} onChange={(v) => setVideo(index, 'videoUrl', v)} />
                    <TextField label="Comment" value={video.comment} onChange={(v) => setVideo(index, 'comment', v)} rows={3} />
                    <Field label="Rating"><StarRating rating={video.rating} onRatingChange={(rating) => setVideo(index, 'rating', rating)} /></Field>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </EditorCard>
  );
};

/* Event localized fields are always stored as { de, en } strings. */
const asLocalizedObject = (value) => {
  const base = isLocalized(value) ? value : { de: value ?? '', en: value ?? '' };
  return { ...base };
};

const readLocalized = (value, lang) => {
  if (!isLocalized(value)) return value || '';
  const v = value[lang];
  return Array.isArray(v) ? v.join('\n') : v || '';
};

const EventsSectionEditor = () => {
  const { section, editLanguage, set, l } = useSectionEditor('eventsSection');
  const events = Array.isArray(section.events) ? section.events : [];

  const setEvents = (items) => set('events', items);

  /** Write into the active language of a localized top-level field. */
  const setLoc = (key, value) =>
    set(key, { ...asLocalizedObject(section[key]), [editLanguage]: value });

  /** Write into the active language of an event field (localized or plain). */
  const setEventField = (idx, field, value) => {
    const item = { ...(events[idx] || {}) };
    item[field] =
      field === 'title' || field === 'description'
        ? { ...asLocalizedObject(item[field]), [editLanguage]: value }
        : value;
    const items = [...events];
    items[idx] = item;
    setEvents(items);
  };

  const addEvent = () =>
    setEvents([
      ...events,
      { id: Date.now(), title: { de: 'Neues Event', en: 'New event' }, date: '', description: { de: '', en: '' }, image: '' },
    ]);

  const handleEventImageUpload = async (e, idx) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const url = await uploadImageFile(file, `event_${Date.now()}`);
      setEventField(idx, 'image', url);
    } catch {
      alert('Upload failed');
    }
    e.target.value = '';
  };

  return (
    <EditorCard title="Events" stateKey="eventsSection">
      <LocalizedTextField label={l('Bereichstitel', 'Section title')} lang={editLanguage} value={readLocalized(section.headerTitle, editLanguage)} onChange={(v) => setLoc('headerTitle', v)} />
      <LocalizedTextField label={l('Bereichsuntertitel', 'Section subtitle')} lang={editLanguage} value={readLocalized(section.headerSubtitle, editLanguage)} onChange={(v) => setLoc('headerSubtitle', v)} />

      <div className="events-list-controls">
        <div className="list-heading">
          <strong>{l('Veranstaltungen', 'Events')}</strong>
          <button type="button" className="btn-add" onClick={addEvent}>{l('Event hinzufügen', 'Add event')}</button>
        </div>

        {events.map((ev, idx) => (
          <div key={ev.id || idx} className="event-item">
            <div className="event-item-row">
              <TextField label={l('Titel', 'Title')} tag={<LangTag lang={editLanguage} />} value={readLocalized(ev.title, editLanguage)} onChange={(v) => setEventField(idx, 'title', v)} />
              <TextField label={l('Datum', 'Date')} type="date" value={ev.date || ''} onChange={(v) => setEventField(idx, 'date', v)} />
              <button type="button" className="btn-remove" onClick={() => setEvents(events.filter((_, i) => i !== idx))}>&times;</button>
            </div>
            <LocalizedTextField label={l('Beschreibung', 'Description')} lang={editLanguage} value={readLocalized(ev.description, editLanguage)} onChange={(v) => setEventField(idx, 'description', v)} rows={3} />
            <Field label={l('Bild', 'Image')}>
              <div className="event-image-upload">
                {ev.image && <img src={ev.image} alt="Event" className="event-image-preview" onError={(e) => { e.target.style.display = 'none'; }} />}
                <input type="text" value={ev.image || ''} placeholder={l('Bild-URL oder hochladen', 'Image URL or upload')} onChange={(v) => setEventField(idx, 'image', v.target.value)} />
                <span className="event-upload-btn">
                  {l('Hochladen', 'Upload')}
                  <input type="file" accept="image/*" onChange={(e) => handleEventImageUpload(e, idx)} />
                </span>
              </div>
            </Field>
          </div>
        ))}
      </div>
    </EditorCard>
  );
};

const ContactSectionEditor = () => {
  const { section, set } = useSectionEditor('contactSection');

  return (
    <EditorCard title="Contact" stateKey="contactSection">
      <TextField label="Execution text" value={section.executionText || ''} onChange={(v) => set('executionText', v)} />
      <TextField label="Contact letters" value={(section.kontaktLetters || []).join('')} placeholder="Word for the contact letters" onChange={(v) => set('kontaktLetters', v.split(''))} />
      <TextField label="Address" value={section.address || ''} onChange={(v) => set('address', v)} />
      <TextField label="Phone" value={section.phone || ''} onChange={(v) => set('phone', v)} />
      <TextField label="Email" value={section.email || ''} onChange={(v) => set('email', v)} />
    </EditorCard>
  );
};

const NewSectionEditor = () => {
  const { section, set } = useSectionEditor('newSection');
  const stackedText = Array.isArray(section.stackedText) ? section.stackedText : [];

  return (
    <EditorCard title="Possibilities Space" stateKey="newSection">
      <TextField
        label="Stacked text"
        value={stackedText.join('\n')}
        placeholder="Each line in a new row"
        rows={4}
        onChange={(v) => set('stackedText', v.split('\n').filter((line) => line.trim() !== ''))}
      />
    </EditorCard>
  );
};

const GlobalColorsEditor = () => {
  const { section, set, l } = useSectionEditor('globalColors');

  return (
    <EditorCard title="Global Colors" stateKey="globalColors">
      <div className="color-grid">
        <ColorField label={l('Titelfarbe', 'Title color')} value={section.titleColor || '#ffffff'} onChange={(v) => set('titleColor', v)} />
        <ColorField label={l('Textfarbe', 'Text color')} value={section.textColor || '#ffffff'} onChange={(v) => set('textColor', v)} />
        <ColorField label={l('Sektions-Titel', 'Section title')} value={section.sectionTitleColor || '#ffffff'} onChange={(v) => set('sectionTitleColor', v)} />
        <ColorField label={l('Fließtext', 'Body text')} value={section.bodyTextColor || '#ffffff'} onChange={(v) => set('bodyTextColor', v)} />
        <ColorField label={l('Art-Sektion Text', 'Art section text')} value={section.artSectionTextColor || '#222222'} onChange={(v) => set('artSectionTextColor', v)} />
        <ColorField label={l('Art-Sektion Rest', 'Art remaining text')} value={section.artSectionRemainingTextColor || '#777777'} onChange={(v) => set('artSectionRemainingTextColor', v)} />
        <ColorField label={l('Hero-Text', 'Hero text')} value={section.heroTextColor || '#ffffff'} onChange={(v) => set('heroTextColor', v)} />
        <ColorField label={l('Kontakt-Text', 'Contact text')} value={section.contactTextColor || '#ffffff'} onChange={(v) => set('contactTextColor', v)} />
      </div>
    </EditorCard>
  );
};

export const SECTION_EDITORS = {
  hero: HeroEditor,
  first: FirstSectionEditor,
  art: ArtSectionEditor,
  now: NowSectionEditor,
  gallery: GallerySectionEditor,
  videos: VideosSectionEditor,
  events: EventsSectionEditor,
  contact: ContactSectionEditor,
  new: NewSectionEditor,
  globalColors: GlobalColorsEditor,
};
